import { BaseLoadingStore } from "app/modules/common/stores/BaseLoadingStore";
import { action, makeObservable, observable, runInAction } from "mobx";
import { inject } from "react-ioc";
import { ApiClients } from "app/modules/company/services/AxiosBaseClient";
import { BidRateSettings } from "app/modules/company/api/clients.api";
import { FormState, FieldState } from "formstate";
import Moment from 'moment'

// Conditional validator for rate and allowedRateRange when enableAutoBid is true
const requiredIfAutoBidEnabled = (getEnableAutoBid: () => boolean) => (value: number | null | undefined) => {
    if (getEnableAutoBid() && (!value || value <= 0)) {
        return "Value must be greater than 0";
    }
    return null;
};

export class AutoBidSettingsStore extends BaseLoadingStore {
    @inject companyApiClients: ApiClients;

    title: string = 'Auto Bid Settings';
    bidRateSettingsFormState: FormState<any> = new FormState({});

    constructor() {
        super();

        makeObservable(this, {
            title: observable,
            bidRateSettingsFormState: observable,
            init: action,
            saveBidRateSettings: action,
            setEnableAutoBid: action
        });
    }

    // Method to handle enableAutoBid changes and set default datetime
    setEnableAutoBid = (enabled: boolean) => {
        this.bidRateSettingsFormState.$.enableAutoBid.value = enabled;
    };

    // Helper method to create BidRateSettings FormState
    private createBidRateSettingsFormState = (rate: number = 0, allowedRateRange: number = 0, enableAutoBid: boolean = false, disableAt: Moment.Moment = null) => {
        const formState = new FormState({
            rate: new FieldState(rate),
            allowedRateRange: new FieldState(allowedRateRange),
            enableAutoBid: new FieldState(enableAutoBid),
            disableAt: new FieldState<Moment.Moment>(disableAt)
        });

        // Apply conditional validation
        formState.$.rate.validators(
            requiredIfAutoBidEnabled(() => formState.$.enableAutoBid.value)
        );
        formState.$.allowedRateRange.validators(
            requiredIfAutoBidEnabled(() => formState.$.enableAutoBid.value)
        );

        return formState;
    };

    init = async () => {
        await this.wrapInitLoadingApiCall(async () => {
            const bidRateSettings = await this.companyApiClients.rateSettingsClient.getBidRateSettings();

            runInAction(() => {
                // Initialize bid rate settings form state
                this.bidRateSettingsFormState = this.createBidRateSettingsFormState(
                    bidRateSettings?.rate || 0,
                    bidRateSettings?.allowedRateRange || 0,
                    bidRateSettings?.enableAutoBid || false,
                    bidRateSettings?.disableAt || null
                );
            });
        });
    }

    saveBidRateSettings = async () => {
        // Validate the form first
        await this.bidRateSettingsFormState.validate();

        // Check if there are validation errors
        if (this.bidRateSettingsFormState.hasError) {
            return;
        }

        await this.wrapLoadingApiCall(async () => {
            // Convert FormState structure back to BidRateSettings for API
            const rate = this.bidRateSettingsFormState.$.rate.value;
            const allowedRateRange = this.bidRateSettingsFormState.$.allowedRateRange.value;
            const enableAutoBid = this.bidRateSettingsFormState.$.enableAutoBid.value;
            const disableAt = this.bidRateSettingsFormState.$.disableAt.value;

            // Convert local time to UTC for server
            const disableAtUTC = disableAt ? Moment(disableAt).utc() : null;

            const bidRateSettings = new BidRateSettings({
                rate: rate || 0,
                allowedRateRange: allowedRateRange || 0,
                enableAutoBid,
                disableAt: disableAtUTC
            });

            await this.companyApiClients.rateSettingsClient.setBidRateSettings(bidRateSettings);
        });
    }
}
